import type { FC } from 'react'

import { LoaderCircle, Table } from '@skytech/ui'
import { Th } from '@skytech/ui/Table/Th'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import { useProfile } from '@/hooks/user'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'

import { ExportToExcel } from '../components/ExportToExcel'
import type { CostObject } from '../resources'

import CostObjectListItem from './CostObjectListItem'

export const CostObjectList: FC<{
  searchFilter: string
  costObjects: CostObject[]
}> = ({ searchFilter, costObjects }) => {
  const translate = useTranslation()
  const profile = useProfile()
  const [, { handleSort }] = useSortParams<keyof CostObject>()

  const { isLoading, isError } = useQuery(
    queries.adminCostObjectList(),
    adminResources.getCostObjectList
  )

  const { visibleList, ListLoaderComponent } = useRenderLimit(costObjects || [], 30)

  if (isLoading) {
    return <LoaderCircle />
  }

  if (isError || costObjects.length === 0) {
    return <NoResults />
  }

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  const excelExportData = costObjects.map(({ telePolicyId, policyIds, ...costObject }) => ({
    ...costObject,
    currency: profile?.currency,
  }))

  return (
    <>
      <ExportToExcel
        data={excelExportData}
        filename={translate('Employees')}
        options={{
          columnFormats: {
            consentGivenDatetime: 'date',
          },
        }}
      />
      <Table rowHeight="tall">
        <thead>
          <tr>
            <Th className="hidden md:table-cell" />
            <Th id="name" onClick={handleSort}>
              <Translation id="name" />
            </Th>

            <Th id="code" onClick={handleSort}>
              <Translation id="contact" />
            </Th>

            <Th id="employeeNo" className="hidden md:table-cell" onClick={handleSort}>
              <Translation id="employee number" />
            </Th>

            <Th id="benefitType" className="hidden md:table-cell" onClick={handleSort}>
              <Translation id="benefit type" />
            </Th>

            <Th id="departmentName" className="hidden md:table-cell" onClick={handleSort}>
              <Translation id="department" />
            </Th>
          </tr>
        </thead>
        <tbody>
          {visibleList.map(item => (
            <CostObjectListItem key={item.id} searchFilter={searchFilter} {...item} />
          ))}
        </tbody>
      </Table>

      <ListLoaderComponent />
    </>
  )
}
